/**
 * Bundle Contents
 * Handles variant option selection, image updates, dynamic pricing,
 * and add-to-cart for bundle products.
 */
(function () {
  'use strict';

  const section = document.querySelector('.bundle-contents');
  if (!section) return;

  const configEl = section.querySelector('[data-bundle-config]');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('[BundleContents] Failed to parse config:', e);
    return;
  }

  const cards = section.querySelectorAll('.bundle-card');
  const atcBtn = section.querySelector('.bundle-contents__atc-btn');

  /**
   * State: track selected option values per product index.
   * e.g. { 0: { Color: 'Purple', Size: 'S' }, 1: { Size: 'XS-S' } }
   */
  const selectedOptions = {};

  /* ──────────────────────────────────────
   * Init
   * ────────────────────────────────────── */
  function init() {
    cards.forEach(function (card) {
      const idx = parseInt(card.dataset.productIndex, 10);
      selectedOptions[idx] = {};

      // Read initial active state from buttons
      card
        .querySelectorAll(
          '.variant-options__swatch--active, .variant-options__pill--active'
        )
        .forEach(function (btn) {
          const group = btn.closest('.variant-options__group');
          if (group) {
            selectedOptions[idx][group.dataset.optionName] = btn.dataset.value;
          }
        });
    });

    bindEvents();
    updatePricing();
  }

  /* ──────────────────────────────────────
   * Events
   * ────────────────────────────────────── */
  function bindEvents() {
    // Single click listener on the section container (event delegation).
    section.addEventListener('click', function (e) {
      // Find the nearest swatch or pill button from the click target.
      const btn = e.target.closest(
        '.variant-options__swatch, .variant-options__pill'
      );

      // Bail if click wasn't on a swatch/pill, or if the button is disabled (sold-out)
      if (!btn || btn.disabled) return;

      // Walk up to find the option group (Color, Size, etc.) and the parent card
      const group = btn.closest('.variant-options__group');
      const card = btn.closest('.bundle-card');
      if (!group || !card) return;

      // Identify which product and which option was changed
      const idx = parseInt(card.dataset.productIndex, 10);
      const optionName = group.dataset.optionName;
      const value = btn.dataset.value;

      // 1. Update internal state
      selectedOptions[idx][optionName] = value;

      // 2. Toggle active class — remove from all siblings, add to clicked variant
      const isSwatch = btn.classList.contains('variant-options__swatch');
      const activeClass = isSwatch
        ? 'variant-options__swatch--active'
        : 'variant-options__pill--active';

      group.querySelectorAll('.' + activeClass).forEach(function (el) {
        el.classList.remove(activeClass);
      });
      btn.classList.add(activeClass);

      // 3. Update the label text (e.g. "Color: Purple" → "Color: Black")
      const label = group.querySelector('[data-option="' + optionName + '"]');
      if (label) label.textContent = value;

      // 4. Find the variant that matches the current combination of selections
      //    and update the card's tracking attribute + swap the product image
      const matchedVariant = findVariant(idx);
      if (matchedVariant) {
        card.dataset.selectedVariantId = matchedVariant.id;
        updateCardImage(card, matchedVariant);
      }

      // 5. Recalculate the bundle total price with the new selection
      updatePricing();
    });

    // ATC button
    if (atcBtn) {
      atcBtn.addEventListener('click', handleAddToCart);
    }
  }

  /**
   * Find the variant matching the current option selections for a product.
   *
   * @param {number} productIndex - Index of the product in config.products
   * @returns {Object|null} The matched variant object, or null if no match
   *
   * @example
   * // Given config.products[0].options:
   * // [
   * //   { name: "Color", position: 1, values: ["Purple", "Black", "Pink"] },
   * //   { name: "Size", position: 2, values: ["S", "M", "L"] }
   * // ]
   * //
   * // And selectedOptions[0]:
   * // { Color: "Purple", Size: "S" }
   * //
   * // Builds selectedValues: ["Purple", "S"]
   * // Then finds and returns the variant whose options array matches:
   * // {
   * //   id: 44123456789001,
   * //   title: "Purple / S",
   * //   price: 5995,
   * //   compareAtPrice: 0,
   * //   available: true,
   * //   options: ["Purple", "S"],
   * //   featuredImage: "//cdn.shopify.com/.../purple-wondher.jpg?width=400"
   * // }
   */
  function findVariant(productIndex) {
    const product = config.products[productIndex];
    if (!product) return null;

    const selected = selectedOptions[productIndex];
    const selectedValues = product.options.map(function (opt) {
      return selected[opt.name];
    });

    return (
      product.variants.find(function (variant) {
        return variant.options.every(function (val, i) {
          return val === selectedValues[i];
        });
      }) || null
    );
  }

  /**
   * Update the product card image when a variant with a featured image is selected.
   * If the variant has no featuredImage, the current image stays unchanged.
   *
   * @param {HTMLElement} card - The .bundle-card element containing the image
   * @param {Object} variant - The matched variant object from config
   * @param {string} [variant.featuredImage] - CDN URL for the variant image (optional)
   *
   * @example
   * // variant:
   * // {
   * //   id: 44123456789001,
   * //   title: "Purple / S",
   * //   featuredImage: "//cdn.shopify.com/.../purple-wondher.jpg?width=400"
   * // }
   * //
   * // Finds <img data-bundle-product-image> inside the card
   * // and sets img.src to the variant's featuredImage URL
   */
  function updateCardImage(card, variant) {
    if (!variant.featuredImage) return;
    const img = card.querySelector('[data-bundle-product-image]');
    if (img) {
      img.src = variant.featuredImage;
    }
  }

  /**
   * Calculate and display bundle pricing.
   * Compare-at (admin) → struck through. Subtotal with bundle discount → red price.
   * Badge shows total percentage off from compare-at to final price.
   *
   * @example
   * // Compare-at: $114.90, Variants: $50.95 + $46.75, Bundle: 5% off
   * // subtotal = $97.70 → discountedTotal = $92.82 → badge: 19% OFF
   */
  function updatePricing() {
    // Compare-at price from placeholder product (static, set in admin)
    const compareTotal = config.compareAtPrice || 0;

    // Sum each bundled product's discounted price. Round per-unit to match
    // Shopify's discount function, which rounds each line item independently.
    const pct = config.discountPercentage > 0 ? config.discountPercentage : 0;
    let discountedTotal = 0;
    config.products.forEach(function (product, idx) {
      const variant = findVariant(idx);
      if (variant) {
        discountedTotal +=
          pct > 0
            ? variant.price - Math.floor((variant.price * pct) / 100)
            : variant.price;
      }
    });

    // Combined savings: compare-at → discounted total
    let totalPercentageOff = 0;
    if (compareTotal > discountedTotal) {
      totalPercentageOff = Math.round(
        ((compareTotal - discountedTotal) / compareTotal) * 100
      );
    }

    // Target existing price elements rendered by product-price.liquid
    const priceContainer =
      document.querySelector('#price-' + config.sectionId) ||
      document.querySelector('.product-price');

    if (!priceContainer) return;

    const saleEl = priceContainer.querySelector('.price-item--sale');
    const compareEl = priceContainer.querySelector('.price-item--regular');
    const badgeEl = priceContainer.querySelector('.price__discount-detail');
    const finalEl = priceContainer.querySelector('.price__final');

    if (compareTotal > 0 && discountedTotal < compareTotal) {
      // Show sale layout
      if (saleEl) saleEl.textContent = formatMoney(discountedTotal);
      if (compareEl) compareEl.textContent = formatMoney(compareTotal);

      if (badgeEl) {
        if (config.percentageOffMessage) {
          badgeEl.textContent = config.percentageOffMessage.replace(
            '__AMOUNT__',
            totalPercentageOff
          );
        } else {
          badgeEl.textContent = totalPercentageOff + '% OFF';
        }
        badgeEl.style.display = '';
      }

      if (finalEl) finalEl.style.display = 'none';

      const saleWrapper = priceContainer.querySelector('.price__sale');
      if (saleWrapper) saleWrapper.style.display = '';
    } else {
      // No savings — regular price only
      if (finalEl) {
        finalEl.textContent = formatMoney(discountedTotal);
        finalEl.style.display = '';
      }
      if (saleEl) saleEl.textContent = formatMoney(discountedTotal);

      const saleWrapper = priceContainer.querySelector('.price__sale');
      if (saleWrapper && finalEl) saleWrapper.style.display = 'none';
      if (badgeEl) badgeEl.style.display = 'none';
    }
  }

  /* ──────────────────────────────────────
   * Format money (cents → formatted string)
   * Uses Shopify money format from config.
   * ────────────────────────────────────── */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.round(cents / 100).toString();
    const format = config.moneyFormat || '${{amount}}';

    return format
      .replace('{{amount}}', addCommas(amount))
      .replace('{{amount_no_decimals}}', addCommas(amountNoDecimals))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace(
        '{{amount_no_decimals_with_comma_separator}}',
        amountNoDecimals.replace(/(\d)(?=(\d{3})+$)/g, '$1.')
      );
  }

  /**
   * Add thousands separators
   */
  function addCommas(str) {
    const parts = str.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,');
    return parts.join('.');
  }

  /* ──────────────────────────────────────
   * Add to Cart
   * ────────────────────────────────────── */

  /**
   * Validate all bundle cards have an available variant selected.
   * Returns { valid: true, items: [...] } or { valid: false, message: '...' }.
   */
  function validateSelections() {
    const items = [];

    for (let i = 0; i < config.products.length; i++) {
      const product = config.products[i];
      const variant = findVariant(i);

      if (!variant) {
        return {
          valid: false,
          message: 'Please select all options for ' + product.title + '.',
        };
      }

      if (!variant.available) {
        return {
          valid: false,
          message:
            product.title +
            ' — ' +
            variant.title +
            ' is sold out. Please choose a different option.',
        };
      }

      items.push(variant);
    }

    return { valid: true, items: items };
  }

  /**
   * Set loading state on the ATC button.
   */
  function setAtcLoading(loading) {
    if (!atcBtn) return;
    atcBtn.disabled = loading;
    atcBtn.classList.toggle('bundle-contents__atc-btn--loading', loading);
  }

  /**
   * Handle add-to-cart click.
   * Validates selections → builds items array with bundle line item properties →
   * POSTs to /cart/add.js → refreshes and opens cart drawer.
   */
  async function handleAddToCart() {
    // 1. Validate
    const result = validateSelections();
    if (!result.valid) {
      console.log('[BundleContents]', result.message);
      return;
    }

    // 2. Build items payload
    const bundleKey = Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    const items = result.items.map(function (variant) {
      return {
        id: variant.id,
        quantity: 1,
        properties: {
          _bundle_id: config.bundleId || '',
          _bundle_name: config.bundleName || '',
          _bundle_key: bundleKey,
          _bundle_size: String(config.products.length),
        },
      };
    });

    // 3. POST to cart
    setAtcLoading(true);

    try {
      const response = await fetch(
        window.Shopify?.routes?.root
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: items }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(function () {
          return {};
        });
        throw new Error(
          errorData.description ||
            errorData.message ||
            'Could not add to cart. Please try again.'
        );
      }

      // 4. Intelligems added bundle to cart custom metric
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: 'bundleAddedToCart' });

      // 5. Success — refresh cart drawer contents, then open it
      document.dispatchEvent(new CustomEvent('nova:cart:refresh'));
      document.dispatchEvent(new CustomEvent('nova:cart:open'));
    } catch (error) {
      console.error('[BundleContents] Add to cart failed:', error);
    } finally {
      setAtcLoading(false);
    }
  }

  /* ──────────────────────────────────────
   * Go
   * ────────────────────────────────────── */
  init();
})();
